import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  detectPWAPlatform,
  getPWAInstallInstructions,
  hasNativeInstallPrompt,
  showNativeInstallPrompt,
  PWAPlatform,
} from '../utils/pwa';
import { apiFetch } from '../apiFetch';
import { analytics } from '../services/analytics';

interface PWAPromptModalProps {
  open: boolean;
  onClose: () => void;
  onInstalled?: () => void;
}

const PWAPromptModal: React.FC<PWAPromptModalProps> = ({
  open,
  onClose,
  onInstalled,
}) => {
  const [installing, setInstalling] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const platform = detectPWAPlatform();
  const instructions = getPWAInstallInstructions();
  const canUseNativePrompt = hasNativeInstallPrompt();
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleInstallClick = async () => {
    if (!canUseNativePrompt) return;

    setInstalling(true);
    try {
      const accepted = await showNativeInstallPrompt();
      if (accepted) {
        // Mark as installed in backend
        await apiFetch(`${apiUrl}/api/account/profile/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pwa_installed: true,
            pwa_platform: platform,
          }),
        });
        analytics.trackEvent('pwa_install', 'conversion', { platform, method: 'native_prompt' });
        onInstalled?.();
        onClose();
      }
    } catch (error) {
      console.error('Error during PWA install:', error);
    } finally {
      setInstalling(false);
    }
  };

  const handleDontAskAgain = async () => {
    setDismissing(true);
    try {
      await apiFetch(`${apiUrl}/api/account/profile/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pwa_prompt_disabled: true }),
      });
    } catch (error) {
      console.error('Error disabling PWA prompt:', error);
    } finally {
      setDismissing(false);
      onClose();
    }
  };

  const handleMarkInstalled = async () => {
    setInstalling(true);
    try {
      await apiFetch(`${apiUrl}/api/account/profile/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pwa_installed: true,
          pwa_platform: platform,
        }),
      });
      analytics.trackEvent('pwa_install', 'conversion', { platform, method: 'manual_confirm' });
      onInstalled?.();
    } catch (error) {
      console.error('Error marking as installed:', error);
    } finally {
      setInstalling(false);
      onClose();
    }
  };

  const getPlatformIcon = (p: PWAPlatform) => {
    switch (p) {
      case 'ios':
        return <PhoneIphoneIcon sx={{ fontSize: 40 }} />;
      case 'android':
        return <PhoneIphoneIcon sx={{ fontSize: 40 }} />;
      default:
        return <PhoneIphoneIcon sx={{ fontSize: 40 }} />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getPlatformIcon(platform)}
          <span>Install the App</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Get the full experience by adding Grocery Price Checker to your home screen.
          It works offline and feels just like a native app!
        </Typography>

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {instructions.title}
        </Typography>

        <List dense>
          {instructions.steps.map((step, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Typography variant="body2" fontWeight="bold">
                  {index + 1}.
                </Typography>
              </ListItemIcon>
              <ListItemText primary={step} />
            </ListItem>
          ))}
        </List>

        {instructions.note && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            {instructions.note}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
        {canUseNativePrompt && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleInstallClick}
            disabled={installing}
            startIcon={installing ? <CircularProgress size={20} /> : null}
          >
            {installing ? 'Installing...' : 'Install Now'}
          </Button>
        )}

        {!canUseNativePrompt && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleMarkInstalled}
            disabled={installing}
            startIcon={<CheckCircleOutlineIcon />}
          >
            I've Added It
          </Button>
        )}

        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ flex: 1 }}
          >
            Maybe Later
          </Button>
          <Button
            variant="text"
            color="inherit"
            onClick={handleDontAskAgain}
            disabled={dismissing}
            sx={{ flex: 1 }}
          >
            {dismissing ? 'Saving...' : "Don't Ask Again"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PWAPromptModal;
